<template>
    <div
        class="flex flex-wrap items-center gap-x-2 gap-y-3 [container-type:inline-size]"
        role="group"
        :aria-label="formAriaLabel"
        @keydown.esc.stop="$emit('cancel')"
    >
        <transition name="fade-transition" mode="out-in" @after-enter="focusCurrentStepControl">
            <!-- Step one: pick a color -->
            <ColorCarousel
                v-if="currentStep === 'picking'"
                key="picking"
                class="highlighter-add-form__field min-w-0 flex-1"
                :size="variant === 'compact' ? 'sm' : 'md'"
                :scroll-step="84"
            >
                <button
                    v-for="paletteColor in paletteColors"
                    :key="paletteColor.slug"
                    type="button"
                    data-role="palette-color"
                    :class="[
                        swatchClasses,
                        selectedColor === paletteColor.color ? 'ring-[3px] ring-inset ring-theme-text' : '',
                    ]"
                    :style="{ background: paletteColor.color }"
                    :title="paletteColor.label"
                    :aria-label="paletteColor.label"
                    :aria-pressed="String(selectedColor === paletteColor.color)"
                    @click="selectColorAndGoToNaming(paletteColor.color)"
                ></button>
            </ColorCarousel>

            <!-- Step two: name it; the chosen color goes back to the palette. An existing
                 highlighter keeps its color: every highlight made with it stores that hex. -->
            <div v-else key="naming" class="highlighter-add-form__field flex min-w-0 flex-1 items-center gap-2">
                <span
                    v-if="isEditingExistingHighlighter"
                    data-role="chosen-color"
                    aria-hidden="true"
                    :class="fixedSwatchClasses"
                    :style="{ background: selectedColor }"
                ></span>
                <button
                    v-else
                    type="button"
                    data-role="chosen-color"
                    :class="swatchClasses"
                    :style="{ background: selectedColor }"
                    :title="localizations[lang].changeHighlighterColor"
                    :aria-label="localizations[lang].changeHighlighterColor"
                    @click="currentStep = 'picking'"
                ></button>
                <input
                    ref="nameInput"
                    v-model="highlighterName"
                    type="text"
                    :placeholder="localizations[lang].highlighterNamePlaceholder"
                    :aria-label="localizations[lang].highlighterNamePlaceholder"
                    class="h-8 min-w-[72px] flex-1 rounded-md border border-theme-border bg-theme-bg px-2 text-[13px] text-theme-text outline-none focus-visible:ring-2 focus-visible:ring-theme-accent"
                    @keydown.enter="saveHighlighter"
                />
            </div>
        </transition>

        <CommitActions
            class="ml-auto"
            save-data-role="save-highlighter"
            :save-label="saveButtonLabel"
            :is-save-disabled="!canSaveHighlighter"
            :should-show-save="currentStep === 'naming'"
            @cancel="$emit('cancel')"
            @save="saveHighlighter"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { ALL_PALETTE } from '../../js/constants/highlightColors';
import generateHighlighterSlug from '../../js/utils/generateHighlighterSlug';
import CommitActions from '../Components/buttons/CommitActions.vue';
import ColorCarousel from '../ColorCarousel.vue';

const SWATCH_CLASSES =
    'tap-target h-5 w-5 flex-shrink-0 cursor-pointer rounded-full border-none transition-opacity duration-150 hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2';
/* The same dot with nothing that invites a click: no pointer, no hover, no focus ring. */
const FIXED_SWATCH_CLASSES = 'h-5 w-5 flex-shrink-0 rounded-full border-none';

export default {
    name: 'HighlighterAddForm',

    components: {
        ColorCarousel,
        CommitActions,
    },

    props: {
        /**
         * Layout variant: 'compact' uses smaller carousel arrows.
         */
        variant: {
            type: String,
            default: 'standard',
            validator: value => ['compact', 'standard'].includes(value),
        },
        /**
         * The custom highlighter being edited. Creating a new one when absent.
         */
        editedHighlighter: {
            type: Object,
            default: null,
        },
    },

    emits: ['added', 'updated', 'cancel'],

    data() {
        return {
            currentStep: this.editedHighlighter ? 'naming' : 'picking',
            selectedColor: this.editedHighlighter?.color || '',
            highlighterName: this.editedHighlighter?.label || '',
        };
    },

    computed: {
        ...mapState(['localizations', 'lang']),
        ...mapGetters(['allHighlighters']),

        paletteColors() {
            return ALL_PALETTE;
        },

        swatchClasses() {
            return SWATCH_CLASSES;
        },

        fixedSwatchClasses() {
            return FIXED_SWATCH_CLASSES;
        },

        isEditingExistingHighlighter() {
            return this.editedHighlighter !== null;
        },

        formAriaLabel() {
            return this.isEditingExistingHighlighter
                ? this.localizations[this.lang].editHighlighter
                : this.localizations[this.lang].newHighlighter;
        },

        saveButtonLabel() {
            return this.isEditingExistingHighlighter
                ? this.localizations[this.lang].save
                : this.localizations[this.lang].add;
        },

        canSaveHighlighter() {
            return Boolean(this.selectedColor) && Boolean(this.highlighterName.trim());
        },
    },

    mounted() {
        this.$nextTick(this.focusCurrentStepControl);
    },

    methods: {
        ...mapActions(['addAndSaveHighlighter', 'updateAndSaveHighlighter']),
        ...mapMutations(['openToast']),

        focusCurrentStepControl() {
            if (this.currentStep === 'picking') {
                const swatches = this.$el.querySelectorAll('[data-role="palette-color"]');
                const chosenIndex = this.paletteColors.findIndex(color => color.color === this.selectedColor);
                swatches[Math.max(chosenIndex, 0)]?.focus();
                return;
            }
            this.$refs.nameInput?.focus();
            this.$refs.nameInput?.select();
        },

        /**
         * @param {string} color - The picked palette color
         */
        selectColorAndGoToNaming(color) {
            this.selectedColor = color;
            this.currentStep = 'naming';
        },

        /**
         * Persists the highlighter. A rejected save is reported in a toast and closes
         * the form without announcing it.
         */
        async saveHighlighter() {
            if (!this.canSaveHighlighter) return;
            try {
                await (this.isEditingExistingHighlighter
                    ? this.updateExistingHighlighter()
                    : this.createNewHighlighter());
            } catch (error) {
                this.openToast({ text: error?.message || this.localizations[this.lang].settingsSaveFailed });
                this.$emit('cancel');
            }
        },

        /**
         * Creates a new custom highlighter and emits the added event.
         * Awaits the persistence so callers that immediately apply the new
         * color to a selection (note creation) see the slug already saved
         * server-side — otherwise the note POST races the settings PUT and
         * gets rejected by the backend's unknown-slug validation.
         */
        async createNewHighlighter() {
            const label = this.highlighterName.trim();
            const existingSlugs = this.allHighlighters.map(c => c.slug);
            const slug = generateHighlighterSlug(label, existingSlugs);
            const addedColor = this.selectedColor;

            await this.addAndSaveHighlighter({ slug, color: addedColor, label });

            this.$emit('added', { slug, color: addedColor });
        },

        async updateExistingHighlighter() {
            const { slug } = this.editedHighlighter;
            const label = this.highlighterName.trim();

            await this.updateAndSaveHighlighter({ slug, label });

            this.$emit('updated', { slug });
        },
    },
};
</script>

<style scoped>
/* The panels this form is dropped into are resizable, so the row breaks on its own
   width rather than the viewport's, with the phone's bottom sheet stacked outright. */
@container (max-width: 422px) {
    .highlighter-add-form__field {
        @apply basis-full;
    }
}

@media screen and (max-width: 570px) {
    .highlighter-add-form__field {
        @apply basis-full;
    }
}
</style>
